import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { notification } from 'antd';

import Auth0RolesPanelContext from '@totem/components/administration/auth0/roles/panel/Auth0RolesPanelContext';
import { IBAuth0Role } from '@totem/components/administration/auth0/roles/types';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { AUTH0_ROLES_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  buildingId?: string;
  buildingName?: string;
  children?: ReactNode;
};

const Auth0RolesPanelContainer = ({
  buildingId,
  buildingName,
  children,
}: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const {
    buildParameters,
    filterUpdateCount,
    pagingUpdateCount,
    sortingUpdateCount,
    isReady,
  } = useContext(InputContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [roles, setRoles] = useState<IBAuth0Role[]>([]);
  const [refreshData, setRefreshData] = useState<boolean>(false);

  useEffect(() => {
    if (isReady) {
      setRefreshData(true);
    }
  }, [filterUpdateCount, pagingUpdateCount, sortingUpdateCount, isReady]);

  useEffect(() => {
    if (!refreshData) {
      return;
    }
    setRefreshData(false);

    setLoading(true);

    const params: Params = buildParameters();

    fetch(AUTH0_ROLES_ENDPOINT, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(params),
    })
      .then((res) => res.json())
      .then((result: IBAuth0Role[]) => {
        setRoles(Array.isArray(result) ? result : []);
      })
      .catch(() => {
        notification.error({ message: 'Failed to load remote access roles.' });
      })
      .finally(() => setLoading(false));
  }, [refreshData, getApiToken, buildParameters]);

  const onRefresh = () => {
    setRefreshData(true);
  };

  return (
    <Auth0RolesPanelContext.Provider
      value={{ loading, roles, onRefresh, buildingId, buildingName }}
    >
      {children}
    </Auth0RolesPanelContext.Provider>
  );
};

export default Auth0RolesPanelContainer;
